import { Seo, StatusState, StructuredData } from '@/components/molecules';
import { ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { breadcrumbSchema, faqSchema } from '@/utils/structuredData';

import { FAQ_TITLE_ID } from './content';
import { useServiceDetail } from './hooks/useServiceDetail';
import {
  ServiceFaqBand,
  ServiceOverviewBand,
  ServiceScopeBand,
  ServiceSiblingsBand,
} from './organisms';

/**
 * One service, in full — the pages behind `/services`.
 *
 * Every service gets the same five blocks in the same order, which is the one
 * rule worth stating about this page: what it is with the figures beside it,
 * what it covers and how it runs, what people ask, what happens around it, and
 * how to get in touch. Component-level consistency is what lets somebody who has read
 * one of these pages read the next in half the time, and it is why the shape
 * lives here rather than in the catalogue — a service that argued for its own
 * arrangement would cost the reader that.
 *
 * What varies between them is entirely content, and all of it is in
 * `content/services.ts`. This file names no service and would not change if a
 * ninth were added.
 *
 * An unmatched slug is answered rather than thrown: `useServiceDetail` returns
 * null and the page draws the same `StatusState` the guide and accessory
 * detail pages use, marked `noindex` so a mistyped URL cannot be indexed in
 * place of a real one. The branch is here, once, and every band below it has a
 * `Service` rather than a maybe.
 *
 * No `handle` export, unlike the other pages. The floating WhatsApp button
 * reads its message from the route (`usePageWhatsAppMessage`), and a route
 * handle is static — it is declared per route module, not per URL, so a single
 * handle on this file would give every service one shared message. The
 * per-service message is passed straight to the buttons that use it instead,
 * in `ServiceOverviewBand` and the closing band, which is the only way it can
 * actually say which service you were reading.
 */
export default function ServicePage() {
  const detail = useServiceDetail();

  if (!detail) {
    return (
      <>
        <Seo
          title="Service not found"
          description="Browse the services Kabex Motors offers in Uganda."
          path="/services"
          noindex
        />
        <StatusState
          tone="empty"
          title="We couldn't find that service"
          description="It may have been renamed. Browse everything we do instead."
          action={{ label: 'All services', to: '/services' }}
        />
      </>
    );
  }

  const { service, pillar, crumbs, schemaCrumbs, path, highlights, siblings } = detail;

  return (
    <>
      <StructuredData data={[breadcrumbSchema([...schemaCrumbs]), faqSchema(service.faqs)]} />
      <Seo title={service.title} description={service.metaDescription} path={path} />

      <PageIntro
        eyebrow={pillar.title}
        title={service.title}
        lede={service.summary}
        crumbs={crumbs}
        slideshow
        highlights={highlights}
        {...(service.tool ? { action: service.tool } : {})}
        whatsapp
      />

      <ServiceOverviewBand service={service} pillarTitle={pillar.title} />

      <ServiceScopeBand service={service} />

      <ServiceFaqBand
        titleId={FAQ_TITLE_ID}
        eyebrow="Questions"
        title={`About ${service.title.toLowerCase()}`}
        lede="The things people ask us about this service, answered here rather than on a phone call."
        items={service.faqs}
        action={{ label: 'All services', to: '/services' }}
      />

      <ServiceSiblingsBand siblings={siblings} pillarTitle={pillar.title} />

      <ImportPromiseBand />
      <QuoteBand
        title={`Ask us about ${service.title.toLowerCase()}`}
        whatsappMessage={service.whatsappMessage}
      />
    </>
  );
}
